import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import type {
  SecretEntrySummary,
  SecretGrant as SharedSecretGrant,
  SecretGrantPermission,
  SecretGrantTargetType,
  SecretKind,
} from '@agentwiki/shared'
import { apiClient } from '../lib/api-client'

export type SecretEntry = SecretEntrySummary
export type SecretGrant = SharedSecretGrant
export type { SecretGrantPermission, SecretGrantTargetType, SecretKind }

export interface SecretFilters {
  environment?: string
  path?: string
  search?: string
  limit?: number
  offset?: number
}

export interface SecretVersion {
  id: string
  version: number
  kind: SecretKind
  description: string | null
  metadata?: Record<string, unknown> | null
  changeReason: string | null
  createdAt: number
  ciphertextHash?: string
}

export interface SecretAuditEvent {
  id?: string
  action?: string
  resourceType?: string
  resourceId?: string
  actorType?: string
  actorId?: string
  createdAt?: number | string
  metadata?: Record<string, unknown>
  [key: string]: unknown
}

function refreshSecretQueries(qc: ReturnType<typeof useQueryClient>, keys: string[]) {
  keys.forEach((key) => qc.invalidateQueries({ queryKey: [key] }))
}

export function useSecrets(filters: SecretFilters) {
  const params = new URLSearchParams()
  if (filters.environment) params.set('environment', filters.environment)
  if (filters.path) params.set('path', filters.path)
  if (filters.search) params.set('search', filters.search)
  if (filters.limit) params.set('limit', String(filters.limit))
  if (filters.offset) params.set('offset', String(filters.offset))
  return useQuery<{ secrets: SecretEntry[]; total: number }>({
    queryKey: ['secrets', filters],
    queryFn: () => apiClient.get(`/api/secrets?${params}`),
  })
}

export function useCreateSecret() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (body: {
      environment: string
      path: string
      key: string
      kind: SecretKind
      value: string
      description?: string | null
      reason?: string
    }) => apiClient.post<SecretEntry>('/api/secrets', body),
    onSuccess: () => refreshSecretQueries(qc, ['secrets', 'secret-versions', 'secret-audit']),
  })
}

export function useUpdateSecret() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, ...body }: {
      id: string
      value?: string
      kind?: SecretKind
      description?: string | null
      expectedVersion: number
      reason: string
    }) => apiClient.patch<SecretEntry>(`/api/secrets/${id}`, body),
    onSuccess: () => refreshSecretQueries(qc, ['secrets', 'secret-versions', 'secret-audit']),
  })
}

export function useDestroySecret() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async ({ id, destroy }: { id: string; destroy?: boolean }): Promise<{ destroyed?: boolean; disabled?: boolean }> => {
      if (destroy) return apiClient.post<{ destroyed: boolean }>(`/api/secrets/${id}/destroy`, {})
      return apiClient.delete<{ disabled: boolean }>(`/api/secrets/${id}`)
    },
    onSuccess: () => refreshSecretQueries(qc, ['secrets', 'secret-audit']),
  })
}

export function useSecretGrants(enabled = true) {
  return useQuery<{ grants: SecretGrant[] }>({
    queryKey: ['secret-grants'],
    queryFn: () => apiClient.get('/api/secrets/grants'),
    enabled,
  })
}

export function useSecretVersions(id?: string, enabled = true) {
  return useQuery<{ versions: SecretVersion[] }>({
    queryKey: ['secret-versions', id],
    queryFn: () => apiClient.get(`/api/secrets/${id}/versions`),
    enabled: enabled && Boolean(id),
  })
}

export function useRollbackSecret() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (body: { id: string; version: number; expectedVersion: number; reason: string }) =>
      apiClient.post(`/api/secrets/${body.id}/rollback`, {
        version: body.version,
        expectedVersion: body.expectedVersion,
        reason: body.reason,
      }),
    onSuccess: () => refreshSecretQueries(qc, ['secrets', 'secret-versions', 'secret-audit']),
  })
}

export function useCreateSecretGrant() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (body: {
      targetType: SecretGrantTargetType
      targetId: string
      targetDisplayName: string
      environment: string
      path: string
      keyPattern: string
      permissions: SecretGrantPermission[]
      expiresAt?: number | null
      reason?: string
    }) => apiClient.post('/api/secrets/grants', body),
    onSuccess: () => refreshSecretQueries(qc, ['secret-grants', 'secret-audit']),
  })
}

export function useRevokeSecretGrant() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => apiClient.delete(`/api/secrets/grants/${id}`),
    onSuccess: () => refreshSecretQueries(qc, ['secret-grants', 'secret-audit']),
  })
}

export function useSecretAudit(
  filters: { environment?: string; key?: string; limit?: number; offset?: number },
  enabled = true,
) {
  const params = new URLSearchParams()
  if (filters.environment) params.set('environment', filters.environment)
  if (filters.key) params.set('key', filters.key)
  if (filters.limit) params.set('limit', String(filters.limit))
  if (filters.offset) params.set('offset', String(filters.offset))
  return useQuery<{ events: SecretAuditEvent[] }>({
    queryKey: ['secret-audit', filters],
    queryFn: () => apiClient.get(`/api/secrets/audit?${params}`),
    enabled,
  })
}

export function useImportSecrets() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (body: { environment: string; path: string; content: string; overwrite: boolean; defaultKind: SecretKind; reason?: string }) =>
      apiClient.post('/api/secrets/import-env', body),
    onSuccess: () => refreshSecretQueries(qc, ['secrets', 'secret-versions', 'secret-audit']),
  })
}

export async function revealSecretValue(id: string, reason: string) {
  return apiClient.post<{ key: string; value: string; version: number }>(`/api/secrets/${id}/reveal`, { reason })
}

export async function exportSecrets(body: { environment: string; path?: string; reveal: boolean; reason?: string }) {
  return apiClient.post<{ content: string; revealed: boolean; count: number }>('/api/secrets/export', body)
}
